import React, { useCallback, useEffect, useMemo } from "react";
import { Container, MainContainer, TalentLogo } from "./account-confirmation.styled";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../store/app-state";
import { useHistory } from "react-router-dom";
import { LoadingIcon } from "../../../components/content/loading/loading.styled";
import { Text } from "../../../components/common";
import { useTheme } from "styled-components";
import { confirmAccount } from "../../../store/auth/auth-actions";
import { useQuery } from "../../../hooks";

const TALENT_LOGO_URL = 'https://talent.dio.me/assets/img/talent-match-onboarding.svg';
interface QueryInterface {
    q1?: string;
    q2?: string;
}

const AccountConfirmation: React.FC = () => {
    const theme = useTheme();
    const history = useHistory();
    const dispatch = useDispatch();
    const [query, setQuery] = useQuery<QueryInterface>({});
    const { t } = useTranslation("private-home-page", { keyPrefix: "account-confirmation.confirmation" });
    const { 
        confirmationLoading: loading, 
        confirmationSuccess: success, 
        confirmationErrorMessage: errorMsg
    } = useSelector((state: AppState) => state.authState.accountConfirmation);
    const { user } = useSelector((state: AppState) => state.authState);

    const channel = useMemo(() => new BroadcastChannel("reload-user"), []);

    const render = useCallback(() => {
        if(success && !loading) {
            return (
                <div style={{display: 'flex', flexDirection: 'column', gap: '2rem'}}>
                    <LoadingIcon size={80} color="primaryBlue"/>
                    <Text
                        fontSize={20}
                        color={theme.colors.primaryBlue.main}
                    >
                        {t("success")}
                    </Text>
                </div>
            )
        }

        if(errorMsg) {
            return (
                <div>
                    <Text
                        fontSize={20}
                        color={theme.colors.error.main}
                    >
                        {t(errorMsg)}
                    </Text>
                </div>
            )
        }

        if(!query.q1 || !query.q2) {
            return (
                <div>
                    <Text
                        fontSize={20}
                        color={theme.colors.error.main}
                    >
                        {t("invalidLink")}
                    </Text>
                </div>
            )
        }
        
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '2rem'}}>
                <LoadingIcon size={80} />
                <Text
                    fontSize={20}
                    color={theme.colors.primaryPurple.main}
                >
                    {t("loading")}
                </Text>
            </div>
        )
    }, [success, loading, errorMsg, query.q1, query.q2, theme, t]);

    useEffect(() => {
        if(
            !loading 
            && !success
            && query.q1
            && query.q2
        ) {
            setQuery({});
            dispatch(confirmAccount(query.q1, query.q2));
        }
    }, [query.q1, query.q2, dispatch, loading, success, setQuery]);

    useEffect(() => {
        if((!loading && success) || user?.is_talent_active) {
            channel.postMessage("reload");
            const TWO_SECONDS = 2000;
            setTimeout(() => {
                history.push("/");
            }, TWO_SECONDS);
        }
    }, [channel, history, loading, success, user?.is_talent_active]);

    return (
        <MainContainer>
            <Container>
                <TalentLogo src={TALENT_LOGO_URL} />
                {render()}
            </Container>
        </MainContainer>
    );
};

export default AccountConfirmation;
